import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import {
  declineInviteByTeamOwner,
  getAllTeams,
  getTeamDetails,
  getTeamPlayerDetailsForSelection,
  setAsCaptain,
  shortlistPlayers,
} from "../services/teamsService";
import { formatTeam, formatTeams } from "../models/teamModel";
import { Team } from "../models/teamModel";
import useAuthStore from "./authStore";
import { toast } from "react-toastify";
import useWebSocketStore from "./webSocketStore";

interface TeamStore {
  teams: any[] | null;
  myTeam: Team | null;

  isBusy: boolean;
  busyMessage: string;

  getTeams: (seasonId: string) => Promise<void>;

  showMyTeam: boolean;
  setShowMyTeam: (showMyTeam: boolean) => void;

  currentView:
    | "teams"
    | "inviteOwner"
    | "invitePlayer"
    | "editTeam"
    | "shortlistPlayers";
  showInviteOwner: () => void;
  showTeams: () => void;
  showInvitePlayer: () => void;
  showEditTeam: () => void;
  showShortlistPlayers: () => void;
  getTeamDetails: (teamId: string) => Promise<void>;
  cancelInvite: (teamId: string, inviteId: string) => Promise<void>;
  setAsCaptain: (playerId: string) => Promise<void>;
  saveShortlistedPlayers: (
    teamId: string,
    shortlistedPlayers: any[]
  ) => Promise<void>;
  shuffleTeams: () => void;

  setTeams: (teams: Team[]) => void;
  setOrderedTeamIds: (teamIds: string[]) => void;
  draftPlayerIntoMyTeam: (seasonId: string, player: any) => void;
  teamDetailsForSelection: any;
  getTeamDetailsForPlayerSelection: (
    teamId: string,
    matchId: string
  ) => Promise<void>;
}

export const useTeamsStore = create<TeamStore>()(
  devtools(
    persist(
      (set, get) => ({
        teams: null,
        myTeam: null,
        busyMessage: "",
        isBusy: false,
        currentView: "teams",
        showMyTeam: false,
        showInviteOwner: () => set({ currentView: "inviteOwner" }),
        showInvitePlayer: () => set({ currentView: "invitePlayer" }),
        showEditTeam: () => set({ currentView: "editTeam" }),
        showTeams: () => set({ currentView: "teams" }),
        showShortlistPlayers: () => set({ currentView: "shortlistPlayers" }),
        setShowMyTeam: (showMyTeam: boolean) => set({ showMyTeam }),
        getTeams: async (seasonId: string) => {
          try {
            set({ isBusy: true, busyMessage: "Fetching Teams..." });
            const response = await getAllTeams(seasonId);
            if (response.code && response.code === 3008) {
              const teams = formatTeams(response.result);
              const currentUser = useAuthStore.getState().currentUser;
              const myTeam = teams.find(
                (t) =>
                  t.ownersColl.some(
                    (o: any) => o.playerId === currentUser?.playerId
                  ) || t.captain?.playerId === currentUser?.playerId
              );
              set({ teams, isBusy: false, busyMessage: "", myTeam });
              return;
            } else {
              set({
                teams: null,
                isBusy: false,
                busyMessage: "Error fetching Teams",
              });
              toast.error(response.message);
            }
          } catch (error) {
            set({
              teams: null,
              isBusy: false,
              busyMessage: "Error fetching Teams",
            });
            console.error("Error fetching Team:", error);
          }
        },
        getTeamDetails: async (teamId: string) => {
          try {
            set({ isBusy: true, busyMessage: "Fetching Team Details..." });
            const response = await getTeamDetails(teamId);
            if (response.code && response.code === 3026) {
              const team = formatTeam(response.result);
              const currentUser = useAuthStore.getState().currentUser;
              const isMyTeam =
                team.ownersColl.some(
                  (o: any) => o.playerId === currentUser?.playerId
                ) || team.captain?.playerId === currentUser?.playerId;

              if (team && isMyTeam) {
                set({ myTeam: team });
              }
              const teams = get().teams;
              if (teams) {
                const teamIndex = teams.findIndex((t) => t.id === team.id);
                if (teamIndex > -1) {
                  teams[teamIndex] = team;
                } else {
                  teams.push(team);
                }
                set({ teams });
              }
              set({ isBusy: false, busyMessage: "" });
              return;
            } else {
              set({
                isBusy: false,
                busyMessage: "",
              });
              toast.error(response.message);
            }
          } catch (error) {
            set({
              isBusy: false,
              busyMessage: "",
            });
            console.error("Error fetching Team Details:", error);
          }
        },
        teamDetailsForSelection: null,
        getTeamDetailsForPlayerSelection: async (
          teamId: string,
          matchId: string
        ) => {
          try {
            set({ isBusy: true, busyMessage: "Fetching Team Details ..." });
            const response = await getTeamPlayerDetailsForSelection(
              teamId,
              matchId
            );
            if (response.code && response.code === 3026) {
              set({
                isBusy: false,
                busyMessage: "",
                teamDetailsForSelection: response.result,
              });
            } else {
              set({
                isBusy: false,
                busyMessage: "",
              });
              toast.error(response.message);
            }
          } catch (error) {
            set({
              isBusy: false,
              busyMessage: "",
            });
            console.error("Error fetching Team Details:", error);
          }
        },
        cancelInvite: async (teamId: string, inviteId: string) => {
          try {
            set({ isBusy: true, busyMessage: "Cancelling Invite..." });
            const response = await declineInviteByTeamOwner(teamId, inviteId);
            if (response.code && response.code === 9025) {
              set({ isBusy: false, busyMessage: "" });
              return;
            } else {
              // notify failure.
              set({
                isBusy: false,
                busyMessage: "",
              });
            }
          } catch (error) {
            set({
              isBusy: false,
              busyMessage: "",
            });
            console.error("Error cancelling Invite:", error);
          }
        },
        setAsCaptain: async (playerId: string) => {
          try {
            const team = get().myTeam;
            if (!team) return;
            set({ isBusy: true, busyMessage: "Setting as Captain..." });
            const response = await setAsCaptain(team.id, playerId);
            if (response.code && response.code === 3021) {
              set({ isBusy: false, busyMessage: "" });
              return;
            } else {
              set({
                isBusy: false,
                busyMessage: "",
              });
              toast.error(response.message);
            }
          } catch (error) {
            set({
              isBusy: false,
              busyMessage: "",
            });
            console.error("Error setting as captain Invite:", error);
          }
        },
        saveShortlistedPlayers: async (
          teamId: string,
          shortlistedPlayers: any[]
        ) => {
          try {
            set({ isBusy: true, busyMessage: "Shortlisting Players..." });
            const response = await shortlistPlayers(teamId, shortlistedPlayers);
            if (response.code && response.code === 3032) {
              set({ isBusy: false, busyMessage: "" });
              return;
            } else {
              set({
                isBusy: false,
                busyMessage: "",
              });
              toast.error(response.message);
            }
          } catch (error) {
            set({
              isBusy: false,
              busyMessage: "",
            });
            console.error("Error shortlisting Players:", error);
            toast.error("Error shortlisting Players");
          }
        },

        shuffleTeams: async () => {
          try {
            const teams = get().teams;
            if (teams) {
              const shuffledTeams = teams.sort(() => Math.random() - 0.5);
              set({ teams: shuffledTeams });
            } else {
              toast.error("No teams to shuffle");
            }
          } catch (error) {
            toast.error("Error shuffling Teams");
            console.error("Error shuffling Teams:", error);
          }
        },
        setTeams: (teams) => set({ teams }),
        setOrderedTeamIds: (teamIds) => {
          console.log("received ordered team ids", teamIds);
          const orderedTeams = get()
            .teams?.slice()
            .sort((a, b) => {
              return teamIds.indexOf(a.id) - teamIds.indexOf(b.id);
            });
          set({ teams: orderedTeams });
        },
        draftPlayerIntoMyTeam: async (seasonId: string, player: any) => {
          try {
            set({ isBusy: true, busyMessage: "Adding Player to Team..." });

            const teamId = get().myTeam?.id;
            if (!teamId) {
              set({ isBusy: false, busyMessage: "" });
              return;
            }
            const user = useAuthStore.getState().currentUser;
            if (!user) {
              set({ isBusy: false, busyMessage: "" });
              return;
            }

            useWebSocketStore
              .getState()
              .draftPick(
                seasonId,
                player.playerId,
                teamId,
                user.playerId ? user.playerId : ""
              );
            set({ isBusy: false, busyMessage: "" });
          } catch (error) {
            set({
              isBusy: false,
              busyMessage: "",
            });
            console.error("Error adding Player to Team:", error);
            toast.error("Error adding Player to Team");
          }
        },
      }),
      { name: "teamStore", storage: createJSONStorage(() => localStorage) }
    ),
    { name: "TeamStore", enabled: process.env.NODE_ENV === "development" }
  )
);
